import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Body,
  ErrorBanner,
  FlashBanner,
  Header,
  PageWrapper,
  ScopeTab,
  ScopeTabs,
  SecondaryButton,
  Title,
} from "./IntakeReviewStyled";
import QueueList from "./components/QueueList";
import ReviewCard from "./components/ReviewCard";
import {
  closeIntakeReviewCard,
  fetchIntakeReviewQueue,
  openIntakeReviewCard,
  refreshIntakeReviews,
  setIntakeReviewScope,
} from "../../slice/IntakeReviewSlice";

const SCOPES = [
  { key: "pending", label: "To decide" },
  { key: "mine", label: "Assigned to me" },
  { key: "decided", label: "Decided" },
];

/**
 * Intake attorney review — the sign-up / turn-down queue.
 *
 * Two-view shell (pile -> card) rather than a tab bar, because the
 * workflow is linear per intake: pick one off the list, decide it, come
 * back. Litify's version has no list at all — it hands the attorney one
 * intake at a time from a loop, which is why it needed a Skip button and
 * a separate quarantine list just to let him choose what to work on.
 */
const IntakeReview = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { view, scope, card, queue, refreshing, refreshError, flash } =
    useSelector(
      (state) => state.intakeReviews
    );
  const deepLinked = useRef(false);

  useEffect(() => {
    dispatch(fetchIntakeReviewQueue());
  }, [dispatch, scope]);

  // The text message's link lands on one card (Matthew, 2026-10-31: the
  // PI reviewer reads the text, taps, decides). `?card=512` opens it
  // straight away, once, then drops out of the address so a refresh
  // does not reopen a card that was decided.
  useEffect(() => {
    if (deepLinked.current) return;
    const id = new URLSearchParams(location.search).get("card");
    if (!id) return;
    deepLinked.current = true;
    dispatch(openIntakeReviewCard(Number(id)));
    navigate(location.pathname, { replace: true });
  }, [dispatch, location.pathname, location.search, navigate]);

  // A hard refresh can land here in card view with nothing in memory.
  useEffect(() => {
    if (view === "card" && !card) {
      dispatch(closeIntakeReviewCard());
    }
  }, [dispatch, view, card]);

  const counts = queue.counts || {};

  return (
    <PageWrapper>
      <Header>
        <Title>Intake review</Title>
        <ScopeTabs>
          {/* The poll runs every five minutes, which is no help to
              someone looking at an empty queue right now. */}
          <SecondaryButton
            type="button"
            onClick={() => dispatch(refreshIntakeReviews())}
            disabled={refreshing}
            style={{ marginRight: 8, padding: "6px 12px", fontSize: 13 }}
          >
            {refreshing ? "Checking…" : "Check for new intakes"}
          </SecondaryButton>
          {SCOPES.map(({ key, label }) => {
            const count =
              key === "pending"
                ? counts.pending
                : key === "mine"
                ? counts.mine
                : undefined;
            return (
              <ScopeTab
                key={key}
                type="button"
                $active={scope === key}
                aria-pressed={scope === key}
                onClick={() => {
                  dispatch(closeIntakeReviewCard());
                  dispatch(setIntakeReviewScope(key));
                }}
              >
                {label}
                {typeof count === "number" && count > 0 ? ` · ${count}` : ""}
              </ScopeTab>
            );
          })}
        </ScopeTabs>
      </Header>
      <Body>
        {refreshError ? (
          <ErrorBanner role="alert">{refreshError}</ErrorBanner>
        ) : null}
        {flash ? <FlashBanner role="status">{flash}</FlashBanner> : null}
        {view === "card" && card ? <ReviewCard /> : <QueueList />}
      </Body>
    </PageWrapper>
  );
};

export default IntakeReview;
